import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Tabs, Tab, Typography, Chip, Stack, TextField,
  CircularProgress, Alert, Paper,
} from '@mui/material';
import axios from 'axios';
import { API_URL } from '../config';
import useAuth from '../hooks/useAuth';
import UsersTable from '../components/admin/UsersTable';
import DataArtifactsPanel from '../components/admin/DataArtifactsPanel';
import DemoUsersPanel from '../components/admin/DemoUsersPanel';
import EntitiesPanel from '../components/admin/EntitiesPanel';

const STATUS_FILTERS = [
  { label: 'All', value: null },
  { label: 'Pending', value: 'pending' },
  { label: 'Active', value: 'active' },
  { label: 'Suspended', value: 'suspended' },
];

const AdminPage = () => {
  const { isAdmin, enterViewAs } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('users');
  const [statusFilter, setStatusFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [counts, setCounts] = useState({ pending: 0, active: 0, suspended: 0, all: 0 });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (search.trim()) params.q = search.trim();
      const { data } = await axios.get(`${API_URL}/api/admin/users`, { params });
      setUsers(data.users || []);

      // Refresh status counts (independent of the filter the user sees).
      // One unfiltered call, count locally.
      if (!statusFilter && !search.trim()) {
        const counts = { pending: 0, active: 0, suspended: 0, all: data.total };
        for (const u of data.users) counts[u.status] = (counts[u.status] || 0) + 1;
        setCounts(counts);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(detail || e.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => {
    if (isAdmin) fetchUsers();
  }, [isAdmin, fetchUsers]);

  const handleViewAs = async (target) => {
    await enterViewAs({
      user_id: target.user_id,
      email: target.email,
      name: target.preferred_name || target.name || target.email,
    });
    navigate('/');
  };

  const handleSaved = (updated) => {
    setUsers((prev) =>
      prev.map((u) => (u.user_id === updated.user_id ? { ...u, ...updated } : u))
    );
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Admin access required.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 0.5 }}>Admin</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        User management, view-as, insights queries.
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab value="users" label="Users" />
        <Tab value="entities" label="Entities" />
        <Tab value="artifacts" label="Data Artifacts" />
        <Tab value="demo_users" label="Demo Users" />
        <Tab value="insights" label="Insights" disabled />
      </Tabs>

      {tab === 'users' && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
            {STATUS_FILTERS.map((f) => (
              <Chip
                key={f.label}
                label={
                  f.value === null
                    ? `${f.label} (${counts.all})`
                    : `${f.label} (${counts[f.value] || 0})`
                }
                color={statusFilter === f.value ? 'primary' : 'default'}
                onClick={() => setStatusFilter(f.value)}
                clickable
              />
            ))}
            <Box sx={{ flexGrow: 1 }} />
            <TextField
              size="small"
              placeholder="Search email or name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 240 }}
            />
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <UsersTable
              users={users}
              onSaved={handleSaved}
              onViewAs={handleViewAs}
            />
          )}
        </Paper>
      )}

      {tab === 'entities' && <EntitiesPanel />}

      {tab === 'artifacts' && <DataArtifactsPanel />}

      {tab === 'demo_users' && <DemoUsersPanel />}

      {tab === 'insights' && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Athena query interface — coming next.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default AdminPage;
